/**
 * FlexSearch統合モジュール
 *
 * 作品とイベントデータの全文検索機能を提供します。
 * 多言語対応（日本語・英語）で、タイトル・説明文・制作者名などを検索対象とします。
 */

import FlexSearch from 'flexsearch';
import type { Work, Event } from '@/types/domain';
import { getLocalizedText } from './i18n-helpers';

/**
 * FlexSearch Index型定義
 */
type SearchIndex = FlexSearch.Index;

/**
 * 検索対象言語
 */
type SearchLanguage = 'ja' | 'en';

/**
 * 汎用キャッシュクラス
 *
 * データ参照とオプションのキーが同一の場合にキャッシュ値を返す。
 * TExtra = null の場合はデータ参照のみでキャッシュを識別する。
 */
class SearchCache<TData, TExtra, TValue> {
  private entry: { data: TData[]; extra: TExtra; value: TValue } | null = null;

  get(data: TData[], extra: TExtra): TValue | null {
    if (
      this.entry !== null &&
      this.entry.data === data &&
      this.entry.extra === extra
    ) {
      return this.entry.value;
    }
    return null;
  }

  set(data: TData[], extra: TExtra, value: TValue): void {
    this.entry = { data, extra, value };
  }
}

// --- モジュールレベルのインデックスキャッシュ ---
// データ参照と言語が同一ならキャッシュを再利用し、再構築をスキップする。

const worksIndexCache = new SearchCache<Work, SearchLanguage, SearchIndex>();
const eventsIndexCache = new SearchCache<Event, SearchLanguage, SearchIndex>();
const timelineIndexCache = new SearchCache<
  import('@/types/domain').TimelineItem,
  SearchLanguage,
  SearchIndex
>();

// --- IDルックアップMap のキャッシュ ---
// 同一データ参照に対して繰り返し検索される場合、Mapの再構築をスキップ。

const worksMapCache = new SearchCache<Work, null, Map<string, Work>>();
const eventsMapCache = new SearchCache<Event, null, Map<string, Event>>();
const timelineMapCache = new SearchCache<
  import('@/types/domain').TimelineItem,
  null,
  Map<string, import('@/types/domain').TimelineItem>
>();

/**
 * index.search() の戻り値を安全に string[] へ変換する型ガード
 */
function toStringArray(results: unknown): string[] {
  if (!Array.isArray(results)) return [];
  return results.filter((item): item is string => typeof item === 'string');
}

/**
 * 作品用の検索インデックスを作成
 *
 * @param works - 作品データの配列
 * @param language - 検索対象言語
 * @returns FlexSearch インデックス
 */
export function createWorksSearchIndex(
  works: Work[],
  language: SearchLanguage = 'ja'
): SearchIndex {
  const cached = worksIndexCache.get(works, language);
  if (cached) return cached;

  const index = new FlexSearch.Index({
    tokenize: 'full',
    charset: language === 'ja' ? 'cjk' : 'latin',
  });

  works.forEach((work) => {
    const keywords = work.keywords || [];
    const groups = work.group || [];

    // 検索対象フィールドを結合
    const searchableText = [
      getLocalizedText(work.title, language),
      work.description ? getLocalizedText(work.description, language) : '',
      // ジャンル
      work.genre || '',
      // 発表形態（印刷物のみ）
      work.presentation || '',
      // 技法・媒体
      work.artMedium || '',
      // キーワード
      keywords.join(' '),
      // グループ
      groups.join(' '),
      // Phase 1・2 拡張メタデータ
      work.contributor || '',
      work.award || '',
      work.alternativeHeadline || '',
    ]
      .filter(Boolean)
      .join(' ');

    index.add(work.id, searchableText);
  });

  worksIndexCache.set(works, language, index);
  return index;
}

/**
 * イベント用の検索インデックスを作成
 *
 * @param events - イベントデータの配列
 * @param language - 検索対象言語
 * @returns FlexSearch インデックス
 */
export function createEventsSearchIndex(
  events: Event[],
  language: SearchLanguage = 'ja'
): SearchIndex {
  const cached = eventsIndexCache.get(events, language);
  if (cached) return cached;

  const index = new FlexSearch.Index({
    tokenize: 'full',
    charset: language === 'ja' ? 'cjk' : 'latin',
  });

  events.forEach((event) => {
    // 検索対象フィールドを結合
    const searchableText = [
      getLocalizedText(event.title, language),
      event.location ? getLocalizedText(event.location, language) : '',
      event.eventType || '',
      // UI用JSONのworkFeaturedはID配列なので検索対象外
    ]
      .filter(Boolean)
      .join(' ');

    index.add(event.id, searchableText);
  });

  eventsIndexCache.set(events, language, index);
  return index;
}

/**
 * 作品を検索
 *
 * @param query - 検索クエリ
 * @param index - FlexSearch インデックス
 * @param works - 作品データの配列
 * @param limit - 最大結果数（デフォルト: 100）
 * @returns 検索結果の作品配列
 */
export function searchWorks(
  query: string,
  index: SearchIndex,
  works: Work[],
  limit = 100
): Work[] {
  if (!query.trim()) {
    return works;
  }

  // 検索実行（IDの配列を取得）
  const resultIds = toStringArray(index.search(query, { limit }));

  // IDから作品オブジェクトを取得（キャッシュ済みMapを利用）
  let worksMap = worksMapCache.get(works, null);
  if (!worksMap) {
    worksMap = new Map(works.map((work) => [work.id, work]));
    worksMapCache.set(works, null, worksMap);
  }

  return resultIds
    .map((id) => worksMap.get(id))
    .filter((work): work is Work => work !== undefined);
}

/**
 * イベントを検索
 *
 * @param query - 検索クエリ
 * @param index - FlexSearch インデックス
 * @param events - イベントデータの配列
 * @param limit - 最大結果数（デフォルト: 100）
 * @returns 検索結果のイベント配列
 */
export function searchEvents(
  query: string,
  index: SearchIndex,
  events: Event[],
  limit = 100
): Event[] {
  if (!query.trim()) {
    return events;
  }

  // 検索実行（IDの配列を取得）
  const resultIds = toStringArray(index.search(query, { limit }));

  // IDからイベントオブジェクトを取得（キャッシュ済みMapを利用）
  let eventsMap = eventsMapCache.get(events, null);
  if (!eventsMap) {
    eventsMap = new Map(events.map((event) => [event.id, event]));
    eventsMapCache.set(events, null, eventsMap);
  }

  return resultIds
    .map((id) => eventsMap.get(id))
    .filter((event): event is Event => event !== undefined);
}

/**
 * 作品IDのセットから作品オブジェクトを取得
 * （フィルタリング結果と検索結果の積集合を取る際に使用）
 *
 * @param ids - 作品IDのセット
 * @param works - 作品データの配列
 * @returns 作品の配列
 */
export function getWorksByIds(ids: Set<string>, works: Work[]): Work[] {
  return works.filter((work) => ids.has(work.id));
}

/**
 * イベントIDのセットからイベントオブジェクトを取得
 * （フィルタリング結果と検索結果の積集合を取る際に使用）
 *
 * @param ids - イベントIDのセット
 * @param events - イベントデータの配列
 * @returns イベントの配列
 */
export function getEventsByIds(ids: Set<string>, events: Event[]): Event[] {
  return events.filter((event) => ids.has(event.id));
}

/**
 * タイムライン用の検索インデックスを作成
 *
 * WorkとEventを統合してインデックス化
 *
 * @param items - タイムラインアイテムの配列
 * @param language - 検索対象言語
 * @returns FlexSearch インデックス
 */
export function createTimelineSearchIndex(
  items: import('@/types/domain').TimelineItem[],
  language: SearchLanguage = 'ja'
): SearchIndex {
  const cached = timelineIndexCache.get(items, language);
  if (cached) return cached;

  const index = new FlexSearch.Index({
    tokenize: 'full',
    charset: language === 'ja' ? 'cjk' : 'latin',
  });

  items.forEach((item) => {
    let searchableText = '';

    // タイトル
    searchableText += getLocalizedText(item.title, language) + ' ';

    // データ型に応じて追加フィールドを検索対象に含める
    if (item.type === 'work' && 'description' in item.data) {
      const work = item.data as Work;
      if (work.description) {
        searchableText += getLocalizedText(work.description, language) + ' ';
      }
      // ジャンル
      if (work.genre) {
        searchableText += work.genre + ' ';
      }
      // 発表形態（印刷物のみ）
      if (work.presentation) {
        searchableText += work.presentation + ' ';
      }
      // 技法・媒体
      if (work.artMedium) {
        searchableText += work.artMedium + ' ';
      }
      // キーワード
      if (work.keywords) {
        searchableText += work.keywords.join(' ') + ' ';
      }
      // グループ
      if (work.group) {
        searchableText += work.group.join(' ') + ' ';
      }
      // Phase 1・2 拡張メタデータ
      if (work.contributor) {
        searchableText += work.contributor + ' ';
      }
      if (work.award) {
        searchableText += work.award + ' ';
      }
      if (work.alternativeHeadline) {
        searchableText += work.alternativeHeadline + ' ';
      }
    } else if (item.type === 'event' && 'location' in item.data) {
      const event = item.data as Event;
      if (event.location) {
        searchableText += getLocalizedText(event.location, language) + ' ';
      }
      if (event.eventType) {
        searchableText += event.eventType + ' ';
      }
    }

    index.add(item.id, searchableText.trim());
  });

  timelineIndexCache.set(items, language, index);
  return index;
}

/**
 * タイムラインアイテムを検索
 *
 * @param query - 検索クエリ
 * @param index - FlexSearch インデックス
 * @param items - タイムラインアイテムの配列
 * @param limit - 最大結果数（デフォルト: 100）
 * @returns 検索結果のタイムラインアイテム配列
 */
export function searchTimelineItems(
  query: string,
  index: SearchIndex,
  items: import('@/types/domain').TimelineItem[],
  limit = 100
): import('@/types/domain').TimelineItem[] {
  if (!query.trim()) {
    return items;
  }

  // 検索実行（IDの配列を取得）
  const resultIds = toStringArray(index.search(query, { limit }));

  // IDからタイムラインアイテムを取得（キャッシュ済みMapを利用）
  let itemsMap = timelineMapCache.get(items, null);
  if (!itemsMap) {
    itemsMap = new Map(items.map((item) => [item.id, item]));
    timelineMapCache.set(items, null, itemsMap);
  }

  return resultIds
    .map((id) => itemsMap.get(id))
    .filter(
      (item): item is import('@/types/domain').TimelineItem =>
        item !== undefined
    );
}
